import { firebaseService } from './firebase.js';
import { userService } from './user.js';

class AgencyService {
  constructor() {
    this.currentAgency = null;
    this.listeners = [];
    this.activeSubscription = null;
  }

  // ==================== INITIALIZATION ====================

  async initialize() {
    try {
      if (userService.isAgency() && userService.getCurrentAgency()) {
        this.currentAgency = userService.getCurrentAgency();
        this.setupRealtimeSubscription();
        this.notifyListeners();
      }
    } catch (error) {
      // silent
    }
  }

  setupRealtimeSubscription() {
    if (this.activeSubscription) {
      firebaseService.unsubscribe(this.activeSubscription);
    }

    if (!userService.isAgency() || !this.currentAgency) {
      return;
    }

    const documentPath = `agencies/${this.currentAgency.id}`;
    
    this.activeSubscription = firebaseService.subscribeToDocument(
      documentPath,
      (agency) => {
        if (agency) {
          this.currentAgency = agency;
          this.notifyListeners();
        }
      }
    );
  }

  // ==================== AGENCY PROFILE MANAGEMENT ====================

  async updateAgencyProfile(updates) {
    if (!userService.canManageAgencySettings()) {
      throw new Error('Permissão negada para atualizar configurações da agência');
    }

    try {
      const updatedAgency = await firebaseService.updateAgency(this.currentAgency.id, updates);
      this.currentAgency = { ...this.currentAgency, ...updatedAgency };
      this.notifyListeners();
      return this.currentAgency;
    } catch (error) {
      throw error;
    }
  }

  // ==================== AGENCY SETTINGS ====================

  async updateSettings(newSettings) {
    if (!userService.canManageAgencySettings()) {
      throw new Error('Permissão negada para atualizar configurações');
    }

    try {
      const updates = {
        settings: {
          ...this.currentAgency.settings,
          ...newSettings
        }
      };

      return await this.updateAgencyProfile(updates);
    } catch (error) {
      throw error;
    }
  }

  // ==================== SUBSCRIPTION MANAGEMENT ====================

  async updateSubscription(subscriptionData) {
    if (!userService.canManageAgencySettings()) {
      throw new Error('Permissão negada para atualizar assinatura');
    }

    try {
      const updates = {
        subscription: {
          ...this.currentAgency.subscription,
          ...subscriptionData
        }
      };

      return await this.updateAgencyProfile(updates);
    } catch (error) {
      throw error;
    }
  }

  isSubscriptionActive() {
    // Importação dinâmica para evitar dependência circular
    // O subscriptionGuard faz verificações completas incluindo:
    // - Status: active, paused, cancelled, refund_requested, expired
    // - Validação de datas (endDate, pauseUntil)
    // - Consistência entre status e isPremium
    try {
      // Verificação básica para fallback
      const userData = userService.currentUser;
      if (!userData) return false;

      const subscriptionStatus = userData.subscriptionStatus || 'none';
      const isPremium = userData.isPremium === true;
      const subscriptionEndDate = userData.subscriptionEndDate;

      // Status que bloqueiam acesso
      const blockedStatus = ['paused', 'refund_requested'];
      if (blockedStatus.includes(subscriptionStatus)) {
        return false;
      }

      // Status cancelado - verificar se ainda está no período
      if (subscriptionStatus === 'cancelled' || subscriptionStatus === 'canceled') {
        if (subscriptionEndDate) {
          const now = new Date();
          const endDate = new Date(subscriptionEndDate);
          return now <= endDate;
        }
        return false;
      }

      // Status ativo - verificar se não expirou
      if (subscriptionStatus === 'active' && isPremium) {
        if (subscriptionEndDate) {
          const now = new Date();
          const endDate = new Date(subscriptionEndDate);
          if (now > endDate) {
            return false;
          }
        }
        return true;
      }

      // Sem status ou desconhecido
      return false;
    } catch (error) {
      return false;
    }
  }

  // ==================== GETTERS ====================

  getCurrentAgency() {
    return this.currentAgency;
  }

  getAgencySettings() {
    return this.currentAgency?.settings || {};
  }

  getAgencySubscription() {
    return this.currentAgency?.subscription || {};
  }

  // ==================== EVENT HANDLING ====================

  notifyListeners() {
    this.listeners.forEach(callback => callback(this.currentAgency));
  }

  // ==================== CLEANUP ====================

  cleanup() {
    if (this.activeSubscription) {
      firebaseService.unsubscribe(this.activeSubscription);
      this.activeSubscription = null;
    }
    
    this.currentAgency = null;
    this.listeners = [];
  }
}

export const agencyService = new AgencyService();